// src/components/ViolinPlot.js

import moment from "jalali-moment";
import React, { useEffect, useState } from "react";
import Plot from "react-plotly.js";
import { hejriDate } from "@helpers/financial_helper";
import { convertDecadesViolin } from "@helpers/chartHelper";

const ViolinPlot = ({ serverData }) => {
  const currentGeorgianYear = moment(
    serverData?.sowingDateRecommendation?.suggestedSowingDate
  ).year();
  const [decades, setDecades] = useState([]);
  const [firstDayOnAxisX, setFirstDayOnAxisX] = useState();
  const [lastDayOnAxisX, setLastDayOnAxisX] = useState();
  const [ranges, setRanges] = useState([]);

  useEffect(() => {
    let sixDecades = [];
    let decade = [];

    if (serverData?.sowingDateRecommendation?.sowingDateRangeForecasts) {
      sixDecades =
        serverData?.sowingDateRecommendation?.sowingDateRangeForecasts?.reduce(
          (decades, item) => {
            decade.push(item);
            if (decade.length === 10) {
              decades.push(decade);
              decade = [];
            }
            return decades;
          },
          []
        );
      setDecades(sixDecades);
    }
  }, [serverData?.sowingDateRecommendation?.sowingDateRangeForecasts]);

  useEffect(() => {
    if (decades.length === 6) {
      setFirstDayOnAxisX(
        moment(`${currentGeorgianYear}-01-01`, "YYYY-MM-DD")
          .add(decades[0][0]["sowingDayOfYear"], "days")
          .toDate()
      );
      setLastDayOnAxisX(
        moment(`${currentGeorgianYear}-01-01`, "YYYY-MM-DD")
          .add(
            decades[5][9]["sowingDayOfYear"] +
              decades[5][9]["harvestDaysAfterSowingDayOfYear"],
            "days"
          )
          .toDate()
      );
    }
  }, [decades]);

  useEffect(() => {
    if (ranges.length === 0 && decades.length > 0) {
      decades.map((decade, dIdx) => {
        const sowingDayOfYearAverage = Math.round(
          decade?.reduce((total, item) => {
            return total + item.sowingDayOfYear;
          }, 0) / 10
        );
        const harvestDaysAfterSowingDayOfYearAverage = Math.round(
          decade?.reduce((total, item) => {
            return total + item.harvestDaysAfterSowingDayOfYear;
          }, 0) / 10
        );

        setRanges((prev) => [
          ...prev,
          {
            Start: moment(`${currentGeorgianYear}-01-01`, "YYYY-MM-DD")
              .add(sowingDayOfYearAverage, "days")
              .toDate(),
            End: moment(`${currentGeorgianYear}-01-01`, "YYYY-MM-DD")
              .add(
                sowingDayOfYearAverage + harvestDaysAfterSowingDayOfYearAverage,
                "days"
              )
              .toDate(),
            EventName: `${moment(`${currentGeorgianYear}-01-01`, "YYYY-MM-DD")
              .add(sowingDayOfYearAverage, "days")
              .toDate()
              .toLocaleDateString("fa-IR")}  تا  ${moment(
              `${currentGeorgianYear}-01-01`,
              "YYYY-MM-DD"
            )
              .add(
                sowingDayOfYearAverage + harvestDaysAfterSowingDayOfYearAverage,
                "days"
              )
              .toDate()
              .toLocaleDateString("fa-IR")}`,
            EventSource: "",
          },
        ]);
      });
    }
  }, [decades]);

  const firstIndex = decades?.map((decade) => decade[0].sowingDayOfYear);

  const getDateFromDayOfYear = (day, year) => {
    const startDate = new Date(year, 0, 1); // تاریخ 1 ژانویه سال مشخص شده
    const resultDate = new Date(startDate);
    resultDate.setDate(startDate.getDate() + day - 1); // تعداد روزهای گذشته از 1 ژانویه را اضافه می‌کند
    return hejriDate(resultDate.toISOString().split("T")[0]); // تاریخ را به فرمت YYYY-MM-DD تبدیل می‌کند
  };

  const year = currentGeorgianYear; // سال میلادی مورد نظر
  const dates = firstIndex.map((day) => getDateFromDayOfYear(day, year));

  const decadesData = {
    labels: convertDecadesViolin(dates.flatMap((date) => Array(10).fill(date))),
    values: [],
  };

  decades.map((decade) => {
    const potens = decade.map(({ performancePotential }) =>
      parseInt(performancePotential)
    );
    decadesData.values.push(...potens);
  });

  // گروه‌ها را به رنگ‌های مختلف تخصیص می‌دهیم
  const groupColors = [
    "rgba(255,99,132,0.5)", // قرمز
    "rgba(54,162,235,0.5)", // آبی
    "rgba(75,192,192,0.5)", // سبز
    "rgba(153,102,255,0.5)", // بنفش
    "rgba(255,159,64,0.5)", // نارنجی
    "rgba(255,99,71,0.5)", // گوجه‌ای
  ];

  // داده‌ها را به صورت جداگانه برای هر گروه ایجاد می‌کنیم
  const groupedData = [];
  const uniqueLabels = [...new Set(decadesData.labels)]; // حذف برچسب‌های تکراری

  uniqueLabels.forEach((label, index) => {
    const groupData = decadesData.values.filter(
      (_, i) => decadesData.labels[i] === label
    );
    groupedData.push({
      type: "violin",
      name: label,
      x: new Array(groupData.length).fill(label), // برچسب‌ها برای گروه
      y: groupData,
      box: { visible: true },
      line: { color: groupColors[index % groupColors.length] },
      fillcolor: groupColors[index % groupColors.length],
      marker: { color: groupColors[index % groupColors.length] },
      points: "all",
      jitter: 0.3,
      pointpos: 0,
    });
  });

  return (
    <Plot
      data={groupedData}
      layout={{
        title: {
          text: "تغییرات پتانسیل عملکرد محصول در مزرعه شما",
          font: {
            size: 13, // اندازه فونت عنوان
          },
        },
        yaxis: {
          zeroline: true,
          title: {
            text: "پتانسیل عملکرد (کیلوگرم در هکتار)",
            standoff: 45, // فاصله عنوان محور Y از محور (به پیکسل)
            font: { size: 13 }, // اندازه فونت عنوان محور Y
          },
          titlefont: { size: 12 },
          tickfont: { size: 12 },
          standoff: 20, // فاصله برچسب محور Y از محور (به پیکسل)
        },
        xaxis: { zeroline: true, titlefont: { size: 10 }, tickangle: -24 },
        boxmode: "group", // برای گروه‌بندی
        margin: { l: 70, r: 25, t: 30, b: 50 }, // تنظیم حاشیه‌ها
        dragmode: false, // غیرفعال کردن امکان کشیدن نمودار
        width: 690, // عرض نمودار به پیکسل
        height: 400, // ارتفاع نمودار به پیکسل
      }}
      config={{
        staticPlot: false, // غیرفعال کردن تمام تعاملات
        displayModeBar: false, // غیرفعال کردن نوار ابزار
      }}
    />
  );
};

export default ViolinPlot;
